import { useTranslation } from 'react-i18next';
import { Save, Sparkles, Settings, SlidersHorizontal, Puzzle } from 'lucide-react';
import { toast } from 'sonner';
import { useMemo, useState, type ReactNode } from 'react';

import {
  usePlatformAiSettings,
  useUpdatePlatformAiSettings
} from '@/pages/admin/api/queries/usePlatformAiSettings';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Page,
  PageContent
} from '@bliks-io/ui';
import {
  AI_FEATURE_KEYS,
  AI_FEATURES,
  AI_MODEL_CONFIGS,
  AiModelSchema,
  DEFAULT_HIGH_MODEL,
  TranscriptionModelSchema,
  isAiTier,
  modelRunsEffort,
  parseAiModel,
  parseAiFeatureOverride,
  type AiConsumerTier,
  type AiFeatureKey,
  type AiFeatureOverride,
  type AiModel,
  type AiTier,
  type TranscriptionModel,
  type UpdatePlatformAiSettingsRequest
} from '@bliks-io/contracts';
import { ContentHeader } from '@/components/content-header';
import { LoadErrorRetry } from '@/components/load-error-retry/LoadErrorRetry';
import { useAppRegistry } from '@/api/queries/useAppRegistry';
import { AI_MODEL_LABELS, TRANSCRIPTION_MODEL_LABELS } from '@/pages/admin/ai-model-labels';
import {
  deriveAppModelGroups,
  inheritedValue,
  pendingOrStored,
  type AppModelGroup,
  type AppModelRow
} from './app-model-rows';
import {
  diffSettings,
  editEffort,
  storedReasoningEfforts,
  type EffortField,
  type FormOverrides
} from './ai-settings-form';
import { ModelSelectRow, type ReasoningEffortSelectProps } from './ModelSelectRow';
import { AppModelSection } from './AppModelSection';
import { tierOptions } from './tier-options';

// Static translation keys per feature so `check:translations` can statically
// verify them without traversing dynamic `${key}` interpolations.
const FEATURE_I18N: Record<AiFeatureKey, { label: string; description: string }> = {
  'bliksy-chat': {
    label: 'adminSettings.globalAi.featureOverride.bliksy-chat.label',
    description: 'adminSettings.globalAi.featureOverride.bliksy-chat.description'
  },
  'bliksy-modify-subagent': {
    label: 'adminSettings.globalAi.featureOverride.bliksy-modify-subagent.label',
    description: 'adminSettings.globalAi.featureOverride.bliksy-modify-subagent.description'
  },
  'step-summary': {
    label: 'adminSettings.globalAi.featureOverride.step-summary.label',
    description: 'adminSettings.globalAi.featureOverride.step-summary.description'
  },
  'chat-title': {
    label: 'adminSettings.globalAi.featureOverride.chat-title.label',
    description: 'adminSettings.globalAi.featureOverride.chat-title.description'
  },
  'chat-summarizer': {
    label: 'adminSettings.globalAi.featureOverride.chat-summarizer.label',
    description: 'adminSettings.globalAi.featureOverride.chat-summarizer.description'
  },
  'decision-eval-judge': {
    label: 'adminSettings.globalAi.featureOverride.decision-eval-judge.label',
    description: 'adminSettings.globalAi.featureOverride.decision-eval-judge.description'
  }
};

// Sentinel select value on orphan rows — the option that clears the stored key.
const REMOVE_VALUE = '__remove__';

export function GlobalAiSettingsPage() {
  const { t } = useTranslation();
  const { data: settings, isLoading, isError, refetch } = usePlatformAiSettings();
  const {
    data: registryApps,
    isLoading: registryLoading,
    isError: registryFailed
  } = useAppRegistry();
  const updateSettings = useUpdatePlatformAiSettings();

  const [overrides, setOverrides] = useState<FormOverrides>({});

  const highModel = overrides.highModel ?? settings?.highModel;
  const lowModel = overrides.lowModel ?? settings?.lowModel;
  const transcriptionModel = overrides.transcriptionModel ?? settings?.transcriptionModel;

  const resolveFeatureValue = (key: AiFeatureKey): AiFeatureOverride | AiConsumerTier => {
    const pending = overrides.featureOverrides?.[key];
    const effective = pending !== undefined ? pending : settings?.featureOverrides?.[key];
    return effective ?? AI_FEATURES[key].defaultTier;
  };

  const pendingAppModels = overrides.appModelOverrides ?? {};
  const storedAppModels = settings?.appModelOverrides ?? {};
  const storedEfforts = storedReasoningEfforts(settings);
  const appModelGroups = useMemo(
    () =>
      deriveAppModelGroups(
        registryApps ?? [],
        settings?.appModelOverrides ?? {},
        settings?.appReasoningEfforts ?? {}
      ),
    [registryApps, settings]
  );

  // Single source of truth for "what would we send on save?". Drives both the
  // disabled state of the Save button and the mutation payload, so the two
  // can never disagree about what counts as a real change.
  const diff = useMemo<UpdatePlatformAiSettingsRequest | null>(
    () => (settings ? diffSettings(overrides, settings) : null),
    [overrides, settings]
  );

  const hasChanges = !!diff && Object.keys(diff).length > 0;

  const ownEffort = (edits: FormOverrides, field: EffortField, key: string) =>
    pendingOrStored(key, edits[field] ?? {}, storedEfforts[field]);

  // A row keeps its own effort only while it pins a model that runs it; a row
  // that follows a tier or its app row has no effort of its own.
  const editModel = (
    field: EffortField,
    key: string,
    pinned: AiFeatureOverride | null,
    edit: (prev: FormOverrides) => FormOverrides
  ) =>
    setOverrides((prev) => {
      const effort = ownEffort(prev, field, key);
      return pinned && !isAiTier(pinned) && (!effort || modelRunsEffort(pinned, effort))
        ? edit(prev)
        : editEffort(edit(prev), field, key, null);
    });

  const handleSave = async () => {
    if (!diff || Object.keys(diff).length === 0) return;
    try {
      await updateSettings.mutateAsync(diff);
      setOverrides({});
      toast.success(t('adminSettings.globalAi.saved'));
    } catch {
      toast.error(t('adminSettings.globalAi.saveFailed'));
    }
  };

  if (isLoading || registryLoading || !settings) {
    return (
      <Page>
        <PageContent>
          {isError && !settings ? (
            <LoadErrorRetry message={t('common.errorOccurred')} onRetry={() => void refetch()} />
          ) : (
            <div className="h-64 bg-muted animate-pulse rounded-lg" />
          )}
        </PageContent>
      </Page>
    );
  }

  const tierModel = (tier: AiTier): AiModel => (tier === 'high' ? highModel! : lowModel!);

  // The effort a row shows: its own while it pins a model, else that of the
  // tier or app row its model comes from — shown there, read-only here. A
  // decision model runs no effort.
  const reasoningSelect = (
    followed: AiFeatureOverride | AiConsumerTier,
    own: { field: EffortField; key: string },
    parent = own
  ): ReasoningEffortSelectProps | undefined => {
    if (followed === 'decision') return undefined;
    const source = isAiTier(followed)
      ? { field: 'tierReasoningEfforts' as const, key: followed, model: tierModel(followed) }
      : { ...parent, model: followed };
    // A tier model the enum dropped runs as the default high model at that
    // model's default, so no level is offered and a stored one renders stale,
    // like the model does.
    const model = parseAiModel(source.model);
    const { reasoningEfforts, defaultReasoningEffort } = model
      ? AI_MODEL_CONFIGS[model]
      : { ...AI_MODEL_CONFIGS[DEFAULT_HIGH_MODEL], reasoningEfforts: [] };
    return {
      effort: ownEffort(overrides, source.field, source.key),
      efforts: reasoningEfforts,
      defaultEffort: defaultReasoningEffort,
      disabled: source.key !== own.key || source.field !== own.field,
      onEffortChange: (effort) =>
        setOverrides((prev) => editEffort(prev, source.field, source.key, effort))
    };
  };

  const tierReasoning = (tier: AiTier) =>
    reasoningSelect(tierModel(tier), { field: 'tierReasoningEfforts', key: tier });

  const setTierModel = (tier: AiTier, model: AiModel) =>
    editModel('tierReasoningEfforts', tier, model, (prev) =>
      tier === 'high' ? { ...prev, highModel: model } : { ...prev, lowModel: model }
    );

  const featureReasoning = (key: AiFeatureKey) =>
    reasoningSelect(
      parseAiFeatureOverride(resolveFeatureValue(key)) ?? AI_FEATURES[key].defaultTier,
      { field: 'featureReasoningEfforts', key }
    );

  const setFeatureOverride = (key: AiFeatureKey, value: AiFeatureOverride | AiConsumerTier) => {
    // null = "no override" — handleSave sends null so the server $unsets the field.
    const next = value === AI_FEATURES[key].defaultTier || value === 'decision' ? null : value;
    editModel('featureReasoningEfforts', key, next, (prev) => ({
      ...prev,
      featureOverrides: { ...(prev.featureOverrides ?? {}), [key]: next }
    }));
  };

  // A slot without its own override follows its app row, effort included.
  const appRowReasoning = (
    row: AppModelRow,
    pinned: AiFeatureOverride | undefined,
    inherited: AiFeatureOverride | AiConsumerTier
  ) => {
    const own = parseAiFeatureOverride(pinned);
    return reasoningSelect(
      own ?? inherited,
      { field: 'appReasoningEfforts', key: row.key },
      { field: 'appReasoningEfforts', key: own ? row.key : (row.inheritsFrom ?? row.key) }
    );
  };

  // Choosing the value a row inherits stores nothing: the key clears and the
  // row keeps following its parent when the parent moves later.
  const setAppModelOverride = (row: AppModelRow, value: string) => {
    const inherited = inheritedValue(row, pendingAppModels, storedAppModels);
    const next: AiFeatureOverride | null =
      value === REMOVE_VALUE || value === inherited ? null : (value as AiFeatureOverride);
    editModel('appReasoningEfforts', row.key, next, (prev) => ({
      ...prev,
      appModelOverrides: { ...(prev.appModelOverrides ?? {}), [row.key]: next }
    }));
  };

  const removeOrphan = (key: string) =>
    setOverrides((prev) => ({
      ...editEffort(prev, 'appReasoningEfforts', key, null),
      appModelOverrides: { ...(prev.appModelOverrides ?? {}), [key]: null }
    }));

  const leadingOptions = (defaultTier: AiConsumerTier) =>
    tierOptions(defaultTier, highModel!, lowModel!);

  // A row shows its effective value; choosing the inherited value clears the
  // key instead of pinning it (see setAppModelOverride). The trigger's clear
  // button covers the case the select cannot: a stored value equal to the
  // inherited one never fires onValueChange, so without it the key would be
  // unclearable.
  const appOverrideRow = (row: AppModelRow, label: ReactNode = row.label) => {
    const inherited = inheritedValue(row, pendingAppModels, storedAppModels);
    const pinned = pendingOrStored(row.key, pendingAppModels, storedAppModels);
    return (
      <ModelSelectRow
        key={row.key}
        label={label}
        value={pinned ?? inherited}
        onValueChange={(v) => setAppModelOverride(row, v)}
        options={AiModelSchema.options}
        labels={AI_MODEL_LABELS}
        leadingOptions={leadingOptions(row.defaultTier)}
        hasValue={pinned !== undefined}
        onClear={() => setAppModelOverride(row, REMOVE_VALUE)}
        reasoning={appRowReasoning(row, pinned, inherited)}
      />
    );
  };

  // No app declares this key any more, so the only sensible edit is removing
  // it — model and effort together; stored values render as stale options.
  const orphanRow = (key: string, label: ReactNode) => {
    const effort = ownEffort(overrides, 'appReasoningEfforts', key);
    return (
      <ModelSelectRow
        key={key}
        label={label}
        value={pendingOrStored(key, pendingAppModels, storedAppModels) ?? REMOVE_VALUE}
        onValueChange={() => removeOrphan(key)}
        options={[]}
        labels={{}}
        leadingOptions={[{ value: REMOVE_VALUE, label: t('common.Remove') }]}
        reasoning={
          effort === undefined
            ? undefined
            : { effort, efforts: [], onEffortChange: () => removeOrphan(key) }
        }
      />
    );
  };

  const groupHeader = (group: AppModelGroup, label: ReactNode) => {
    if (group.appRow) return appOverrideRow(group.appRow, label);
    if (group.appOrphanKey) return orphanRow(group.appOrphanKey, label);
    return <span className="text-base font-medium">{label}</span>;
  };

  // An app opens on its own while a slot pins a model or holds a stale key,
  // so a folded section never hides a slot that runs differently.
  const hasSlotOverrides = (group: AppModelGroup) =>
    group.orphanSlots.length > 0 ||
    group.slotRows.some((row) => storedAppModels[row.key] !== undefined);

  return (
    <Page>
      <PageContent>
        <ContentHeader
          icon={Settings}
          title={t('adminSettings.globalAi.title')}
          description={t('adminSettings.globalAi.description')}
        />

        <div className="flex flex-col gap-6 mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-muted-foreground" />
                <span className="text-lg">{t('adminSettings.globalAi.title')}</span>
              </CardTitle>
              <CardDescription>{t('adminSettings.globalAi.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <ModelSelectRow
                  label={t('adminSettings.globalAi.highModelLabel')}
                  description={t('adminSettings.globalAi.highModelDescription')}
                  value={highModel!}
                  onValueChange={(v) => setTierModel('high', v as AiModel)}
                  options={AiModelSchema.options}
                  labels={AI_MODEL_LABELS}
                  reasoning={tierReasoning('high')}
                />

                <ModelSelectRow
                  label={t('adminSettings.globalAi.lowModelLabel')}
                  description={t('adminSettings.globalAi.lowModelDescription')}
                  value={lowModel!}
                  onValueChange={(v) => setTierModel('low', v as AiModel)}
                  options={AiModelSchema.options}
                  labels={AI_MODEL_LABELS}
                  reasoning={tierReasoning('low')}
                />

                <ModelSelectRow
                  label={t('adminSettings.globalAi.transcriptionModelLabel')}
                  description={t('adminSettings.globalAi.transcriptionModelDescription')}
                  value={transcriptionModel!}
                  onValueChange={(v) =>
                    setOverrides((prev) => ({
                      ...prev,
                      transcriptionModel: v as TranscriptionModel
                    }))
                  }
                  options={TranscriptionModelSchema.options}
                  labels={TRANSCRIPTION_MODEL_LABELS}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <SlidersHorizontal className="w-5 h-5 mr-2 text-muted-foreground" />
                <span className="text-lg">{t('adminSettings.globalAi.featureOverride.title')}</span>
              </CardTitle>
              <CardDescription>
                {t('adminSettings.globalAi.featureOverride.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {AI_FEATURE_KEYS.map((key) => (
                  <ModelSelectRow
                    key={key}
                    label={t(FEATURE_I18N[key].label)}
                    description={t(FEATURE_I18N[key].description)}
                    value={resolveFeatureValue(key)}
                    onValueChange={(v) =>
                      setFeatureOverride(key, v as AiFeatureOverride | AiConsumerTier)
                    }
                    options={AiModelSchema.options}
                    labels={AI_MODEL_LABELS}
                    leadingOptions={leadingOptions(AI_FEATURES[key].defaultTier)}
                    reasoning={featureReasoning(key)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {(registryFailed || appModelGroups.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Puzzle className="w-5 h-5 mr-2 text-muted-foreground" />
                  <span className="text-lg">{t('adminSettings.globalAi.appOverride.title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {registryFailed ? (
                    // Without this the failed fetch renders exactly like "no app
                    // declares AI", and the stored overrides look gone.
                    <p className="text-sm text-destructive">{t('common.errorOccurred')}</p>
                  ) : (
                    appModelGroups.map((group) => (
                      <AppModelSection
                        key={group.appId}
                        label={group.label}
                        initiallyExpanded={hasSlotOverrides(group)}
                        renderHeader={(label) => groupHeader(group, label)}
                        slots={[
                          ...group.slotRows.map((row) => appOverrideRow(row)),
                          ...group.orphanSlots.map(({ key, label }) => orphanRow(key, label))
                        ]}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end">
            <Button onClick={handleSave} loading={updateSettings.isPending} disabled={!hasChanges}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.Save')}
            </Button>
          </div>
        </div>
      </PageContent>
    </Page>
  );
}
